import api from './api';
import { 
  Shipment, 
  ShipmentWithTracking, 
  CreateShipmentDto, 
  UpdateShipmentStatusDto, 
  TrackingEvent, 
  ShipmentFilterParams,
  createShipmentWithHelpers,
  ShipmentStatus
} from '../types/shipment';
import { handleApiError } from '../utils/apiErrorHandler';

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item));
  }
  
  return Object.keys(obj).reduce((acc: any, key) => {
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    acc[snakeKey] = toSnakeCase(obj[key]);
    return acc;
  }, {});
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item));
  }
  
  return Object.keys(obj).reduce((acc: any, key) => {
    const camelKey = key.replace(/(_\w)/g, (k) => k[1].toUpperCase());
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
};

class ShipmentService {
  /**
   * Fetches shipments with optional filters and pagination
   */
  async getShipments(params?: ShipmentFilterParams): Promise<{ data: ShipmentWithTracking[], total: number }> {
    try {
      console.log('[ShipmentService] Fetching shipments with params:', params);
      
      // Convert filter params to snake_case for the API
      const apiParams = params ? toSnakeCase(params) : {};
      
      // Ensure trackingNumber is mapped to tracking_number
      if (params?.trackingNumber && !apiParams.tracking_number) {
        apiParams.tracking_number = params.trackingNumber;
      }
      
      const response = await api.get('/shipments', { params: apiParams });
      
      // Process the response data
      const processShipment = (item: any): ShipmentWithTracking => {
        // Convert all fields to camelCase
        const shipment = toCamelCase(item) as any;
        
        // Ensure tracking_events is an array and copy to trackingEvents if needed
        if (Array.isArray(shipment.tracking_events) && !Array.isArray(shipment.trackingEvents)) {
          shipment.trackingEvents = [...shipment.tracking_events];
        } else if (!Array.isArray(shipment.tracking_events) && !Array.isArray(shipment.trackingEvents)) {
          shipment.tracking_events = [];
          shipment.trackingEvents = [];
        }
        
        // Create a new shipment with helper methods
        return createShipmentWithHelpers(shipment) as ShipmentWithTracking;
      };
      
      let data: ShipmentWithTracking[] = [];
      let total = 0;
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        // Direct array response
        data = response.data.map(processShipment);
        total = response.headers['x-total-count'] ? 
          parseInt(response.headers['x-total-count'], 10) : data.length;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Wrapped array response (e.g., { data: [...], total: X })
        data = response.data.data.map(processShipment);
        total = response.data.total || data.length;
      } else {
        console.warn('[ShipmentService] Unexpected response format, returning empty result');
      }
      
      console.log(`[ShipmentService] Fetched ${data.length} shipments (total: ${total})`);
      return { data, total };
      
    } catch (error) {
      const apiError = handleApiError(error, 'Failed to fetch shipments');
      console.error(`[ShipmentService] Error: ${apiError.message}`, apiError.details);
      throw apiError;
    }
  }

  /**
   * Creates a new shipment
   */
  async createShipment(shipmentData: CreateShipmentDto): Promise<Shipment> {
    try {
      console.log('[ShipmentService] Creating shipment with data:', shipmentData);
      
      // Convert camelCase to snake_case for the API
      const snakeCaseData = toSnakeCase(shipmentData);
      
      const response = await api.post('/shipments', snakeCaseData);
      const createdShipment = toCamelCase(response.data);
      
      console.log('[ShipmentService] Created shipment:', createdShipment);
      return createdShipment;
    } catch (error) {
      console.error('[ShipmentService] Error creating shipment:', error);
      throw handleApiError(error, 'Failed to create shipment');
    }
  }

  async searchShipments(searchTerm: string): Promise<ShipmentWithTracking[]> {
    try {
      if (!searchTerm || typeof searchTerm !== 'string' || searchTerm.trim() === '') {
        console.warn('[ShipmentService] Empty search term provided, returning empty results');
        return [];
      }
      
      // Reuse getShipments with the search term as a filter
      const { data } = await this.getShipments({
        search: searchTerm.trim(),
        _limit: 50,
        _sort: 'created_at:desc'
      });
      
      console.log(`[ShipmentService] Found ${data.length} shipments matching search term: ${searchTerm}`);
      return data;
      
    } catch (error) {
      console.error('[ShipmentService] Error searching shipments:', error);
      throw handleApiError(error, 'Failed to search shipments');
    }
  }

  // Alias for createShipment for backward compatibility
  async addShipment(shipmentData: CreateShipmentDto): Promise<Shipment> {
    return this.createShipment(shipmentData);
  }

  /**
   * Updates a shipment's status by tracking number
   */
  async updateShipmentStatus(
    trackingNumber: string,
    statusData: UpdateShipmentStatusDto
  ): Promise<Shipment> {
    try {
      if (!trackingNumber) {
        throw new Error('Tracking number is required to update status');
      }
      console.log(`[ShipmentService] Updating status for shipment ${trackingNumber} to ${statusData.status}`);
      
      const snakeCaseData = toSnakeCase(statusData);
      // Assuming the backend supports a RESTful endpoint for status updates by tracking number
      const response = await api.put(`/shipments/${trackingNumber}/status`, snakeCaseData);
      
      const updatedShipment = toCamelCase(response.data) as Shipment;
      console.log(`[ShipmentService] Status updated for shipment with tracking number ${trackingNumber}`);
      
      return updatedShipment;
      
    } catch (error) {
      const apiError = handleApiError(error, `Failed to update status for shipment ${trackingNumber}`);
      console.error(`[ShipmentService] Error: ${apiError.message}`, apiError.details);
      throw apiError;
    }
  }

  /**
   * Updates a shipment by tracking number
   */
  async updateShipment(
    trackingNumber: string,
    shipmentData: Partial<CreateShipmentDto>
  ): Promise<Shipment> {
    try {
      console.log('🚀 [ShipmentService] updateShipment called');
      console.log('📋 [ShipmentService] Input parameters:');
      console.log('  - trackingNumber:', trackingNumber, '(type:', typeof trackingNumber, ')');
      console.log('  - shipmentData:', JSON.stringify(shipmentData, null, 2));
      
      if (!trackingNumber) {
        console.error('❌ [ShipmentService] Tracking number is required to update shipment');
        throw new Error('Tracking number is required to update shipment');
      }
      
      console.log('🔄 [ShipmentService] Converting camelCase to snake_case for API...');
      console.log('📊 [ShipmentService] Original data before conversion:', shipmentData);
      
      // Convert camelCase to snake_case for the API
      const snakeCaseData = toSnakeCase(shipmentData);
      
      console.log('✅ [ShipmentService] Converted snake_case data:', JSON.stringify(snakeCaseData, null, 2));
      console.log('🌐 [ShipmentService] About to make PUT request to:', `/shipments/${trackingNumber}`);
      console.log('📤 [ShipmentService] Request payload:', JSON.stringify(snakeCaseData, null, 2));
      
      // Use PUT request to update shipment by tracking number
      console.log('⏳ [ShipmentService] Making API call...');
      const response = await api.put(`/shipments/${trackingNumber}`, snakeCaseData);
      
      console.log('✅ [ShipmentService] API call successful!');
      console.log('📥 [ShipmentService] Response status:', response.status);
      console.log('📥 [ShipmentService] Response data:', JSON.stringify(response.data, null, 2));
      
      console.log('🔄 [ShipmentService] Converting response from snake_case to camelCase...');
      const updatedShipment = toCamelCase(response.data) as Shipment;
      
      console.log('🎉 [ShipmentService] Successfully updated shipment with tracking number:', trackingNumber);
      console.log('📋 [ShipmentService] Final result:', JSON.stringify(updatedShipment, null, 2));
      
      return updatedShipment;
      
    } catch (error) {
      console.error('💥 [ShipmentService] Error in updateShipment:', error);
      console.error('💥 [ShipmentService] Error details:');
      console.error('  - message:', (error as any).message);
      console.error('  - response:', (error as any).response);
      console.error('  - response.data:', (error as any).response?.data);
      console.error('  - response.status:', (error as any).response?.status);
      console.error('  - response.statusText:', (error as any).response?.statusText);
      
      const apiError = handleApiError(error, `Failed to update shipment ${trackingNumber}`);
      console.error('📝 [ShipmentService] Processed API error:', apiError.message, apiError.details);
      throw apiError;
    }
  }

  /**
   * Alias for updateShipment for backward compatibility
   */
  async updateShipmentByTrackingNumber(
    trackingNumber: string,
    shipmentData: Partial<CreateShipmentDto>
  ): Promise<Shipment> {
    return this.updateShipment(trackingNumber, shipmentData);
  }

  /**
   * Deletes a shipment by tracking number.
   */
  async deleteShipment(trackingNumber: string): Promise<void> {
    try {
      if (!trackingNumber) {
        throw new Error('Tracking number is required for deletion');
      }
  
      // The backend should support deletion by trackingNumber. 
      // This simplifies the frontend logic significantly.
      await api.delete(`/shipments/${trackingNumber}`);
  
      console.log(`[ShipmentService] Successfully deleted shipment with tracking number: ${trackingNumber}`);
  
    } catch (error: any) {
      console.error(`[ShipmentService] Error deleting shipment with tracking number ${trackingNumber}:`, error);
      throw handleApiError(error, 'Failed to delete shipment');
    }
  }

  /**
   * Get shipment tracking history by tracking number
   * @param trackingNumber The shipment tracking number
   */
  async getShipmentTrackingHistory(trackingNumber: string): Promise<TrackingEvent[]> {
    try {
      // First get the shipment to ensure the tracking number is valid
      const shipment = await this.getShipment(trackingNumber);
      
      // Then fetch the tracking history using the tracking number
      const response = await api.get(`/shipments/tracking/${trackingNumber}/history`);
      return (response.data || []).map((event: any) => toCamelCase(event) as TrackingEvent);
    } catch (error) {
      console.error(`Error fetching tracking history for shipment ${trackingNumber}:`, error);
      throw handleApiError(error, `Failed to fetch tracking history for shipment ${trackingNumber}`);
    }
  }

  /**
   * Fetch a shipment by tracking number
   * @param trackingNumber - The shipment tracking number
   * @returns The shipment with tracking information
   * @throws {Error} If tracking number is invalid or shipment not found
   */
  async getShipment(trackingNumber: string): Promise<ShipmentWithTracking> {
    try {
      trackingNumber = String(trackingNumber).trim();
      
      if (!trackingNumber) {
        throw new Error('Tracking number is required');
      }
      
      // Fetch shipment by tracking number using query parameter
      const response = await api.get('/shipments', {
        params: { tracking_number: trackingNumber }
      });
      
      if (!Array.isArray(response.data) || response.data.length === 0) {
        throw new Error(`No shipment found with tracking number: ${trackingNumber}`);
      }
      
      // Get the first matching shipment (tracking numbers should be unique)
      const shipmentData = toCamelCase(response.data[0]) as ShipmentWithTracking;
      
      // Fetch tracking history if needed
      if (shipmentData.id) {
        try {
          const historyResponse = await api.get(`/shipments/${shipmentData.id}/tracking-history`);
          if (Array.isArray(historyResponse.data)) {
            shipmentData.tracking_events = historyResponse.data.map(event => ({
              ...toCamelCase(event),
              timestamp: event.timestamp || new Date().toISOString()
            })) as TrackingEvent[];
          }
        } catch (historyError) {
          console.warn('Failed to fetch tracking history:', historyError);
          shipmentData.tracking_events = [];
        }
      } else {
        shipmentData.tracking_events = [];
      }
      
      console.log('[ShipmentService] Successfully fetched shipment by tracking number:', shipmentData);
      return shipmentData;
      
    } catch (error) {
      console.error(`[ShipmentService] Error getting shipment with tracking number ${trackingNumber}:`, error);
      throw handleApiError(error, `Failed to get shipment with tracking number: ${trackingNumber}`);
    }
  }

  /**
   * @deprecated Use getShipment() instead
   */
  async getShipmentByTrackingNumber(trackingNumber: string): Promise<ShipmentWithTracking> {
    console.warn('getShipmentByTrackingNumber is deprecated. Use getShipment() instead.');
    return this.getShipment(trackingNumber);
  }

  /**
   * Track a shipment by tracking number (public endpoint)
   * @deprecated Use getShipment() for standard tracking
   */
  async trackShipment(trackingNumber: string): Promise<ShipmentWithTracking> {
    console.warn('trackShipment is deprecated. Use getShipment() for standard tracking.');
    try {
      const response = await api.get(`/tracking/${trackingNumber}`);
      return toCamelCase(response.data) as ShipmentWithTracking;
    } catch (error) {
      console.error(`Error tracking shipment with tracking number ${trackingNumber}:`, error);
      throw handleApiError(error, `Failed to track shipment ${trackingNumber}`);
    }
  }

  /**
   * @deprecated Use getShipment() with tracking number instead
   */
  async getShipmentById(id: string | number): Promise<ShipmentWithTracking> {
    console.warn('getShipmentById is deprecated. Use getShipment() with tracking number instead.');
    try {
      const response = await api.get(`/shipments/tracking/${id}`);
      return toCamelCase(response.data) as ShipmentWithTracking;
    } catch (error) {
      throw handleApiError(error, `Failed to fetch shipment with ID: ${id}`);
    }
  }

  // Get shipment statistics
  async getStatistics(params?: { start_date?: string; end_date?: string }) {
    const response = await api.get('/shipments/statistics', { params });
    return response.data;
  }
}

export default new ShipmentService();
